import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Palette, ChevronDown, Check, Plus } from "lucide-react";
import { createLazyIcon } from "../../lib/lazy-icons";
import { supabase } from "../../lib/supabase";

const Lock = createLazyIcon("Lock");

const DEFAULT_PRIMARY_COLOR = "#2a5cdb"; // color-exempt: valeur de depart du color picker de branding par sondage, pas du chrome de l'app
const DEFAULT_SECONDARY_COLOR = "#f0f0f0"; // color-exempt

interface Template {
  id: string;
  name: string;
  primary_color: string;
  secondary_color: string;
}

interface BrandingTemplateChipProps {
  templateId: string | null;
  onTemplateChange: (templateId: string | null) => void;
  /** Templates de branding réservés SOLO. */
  locked?: boolean;
}

/**
 * Chip up-front (même famille que CadenceToggle/AudienceChip) pour choisir un
 * modèle de branding à appliquer au sondage. Le choix est appliqué au sondage
 * après sa création (poll-template PATCH), indépendamment de la publication
 * sur la page personnelle.
 */
export function BrandingTemplateChip({
  templateId,
  onTemplateChange,
  locked = false,
}: BrandingTemplateChipProps) {
  const { t } = useTranslation(["creator"]);
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPrimary, setNewPrimary] = useState(DEFAULT_PRIMARY_COLOR);
  const [newSecondary, setNewSecondary] = useState(DEFAULT_SECONDARY_COLOR);
  const [createError, setCreateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (locked) return;
    supabase.functions
      .invoke("templates", { method: "GET" })
      .then(({ data }) => setTemplates(Array.isArray(data) ? data : []));
  }, [locked]);

  function resetCreateForm() {
    setCreating(false);
    setNewName("");
    setNewPrimary(DEFAULT_PRIMARY_COLOR);
    setNewSecondary(DEFAULT_SECONDARY_COLOR);
    setCreateError(null);
  }

  async function handleCreateTemplate() {
    if (!newName.trim()) {
      setCreateError(t("creator:intent.template_name_required"));
      return;
    }
    setSaving(true);
    setCreateError(null);
    const { data, error } = await supabase.functions.invoke("templates", {
      method: "POST",
      body: {
        name: newName.trim(),
        primary_color: newPrimary,
        secondary_color: newSecondary,
      },
    });
    setSaving(false);
    if (error) {
      setCreateError(
        error.message || t("creator:intent.template_create_error"),
      );
      return;
    }
    setTemplates((prev) => [...prev, data as Template]);
    resetCreateForm();
  }

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) resetCreateForm();
  }, [open]);

  const selected = templates.find((tpl) => tpl.id === templateId) ?? null;

  return (
    <div className={open ? "relative z-30" : undefined}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("creator:intent.cap_template")}
        </span>
        {locked && (
          <Link
            to="/pricing"
            className="flex items-center gap-1 text-[11px] text-primary hover:text-primary/80"
            data-testid="template-upgrade-link"
          >
            <Lock className="w-3 h-3" />
            {t("creator:recurrence.upgrade_cta")}
          </Link>
        )}
      </div>

      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          disabled={locked}
          onClick={(e) => {
            e.stopPropagation();
            if (!locked) setOpen((v) => !v);
          }}
          data-testid="template-chip"
          className={`w-full flex items-center gap-3 rounded-xl border bg-card px-3.5 py-2.5 text-left transition-colors ${
            locked
              ? "opacity-50 cursor-not-allowed border-border"
              : selected
                ? "border-primary/40 shadow-[0_0_0_1px_hsl(var(--primary)/0.24)]"
                : "border-border hover:border-muted-foreground"
          }`}
        >
          <span
            className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-muted"
            style={
              selected ? { backgroundColor: selected.primary_color } : undefined
            }
          >
            <Palette className="w-4 h-4 text-primary" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-foreground">
              {selected
                ? selected.name
                : t("creator:intent.template_none_title")}
            </span>
            <span className="block truncate text-[11.5px] text-muted-foreground">
              {t("creator:intent.template_desc")}
            </span>
          </span>
          <ChevronDown className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
        </button>

        {open && (
          <div
            className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 rounded-xl border border-border bg-popover p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
            data-testid="template-popover"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                onTemplateChange(null);
                setOpen(false);
              }}
              data-testid="template-option-none"
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                templateId === null ? "bg-muted" : "hover:bg-muted"
              }`}
            >
              <span className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-card">
                <Palette className="w-4 h-4 text-muted-foreground" />
              </span>
              <span className="min-w-0 flex-1 text-sm font-semibold text-foreground">
                {t("creator:intent.template_none_title")}
              </span>
              {templateId === null && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </button>

            {templates.length === 0 ? (
              <p className="px-3 py-2 text-[11.5px] text-muted-foreground">
                {t("creator:intent.template_empty")}
              </p>
            ) : (
              templates.map((tpl) => {
                const isSelected = tpl.id === templateId;
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => {
                      onTemplateChange(tpl.id);
                      setOpen(false);
                    }}
                    data-testid={`template-option-${tpl.id}`}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                      isSelected ? "bg-muted" : "hover:bg-muted"
                    }`}
                  >
                    <span
                      className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg border border-border"
                      style={{ backgroundColor: tpl.primary_color }}
                    />
                    <span className="min-w-0 flex-1 text-sm font-semibold text-foreground">
                      {tpl.name}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                    )}
                  </button>
                );
              })
            )}

            <div className="border-t border-border mt-1 pt-1">
              {!creating ? (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  data-testid="template-create-cta"
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-primary hover:bg-muted"
                >
                  <Plus className="h-4 w-4" />
                  {t("creator:intent.template_create_cta")}
                </button>
              ) : (
                <div
                  className="space-y-2 px-3 py-2"
                  data-testid="template-create-form"
                >
                  <div>
                    <label className="block text-[11px] text-muted-foreground mb-1">
                      {t("creator:intent.template_name_label")}
                    </label>
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={t(
                        "creator:intent.template_name_placeholder",
                      )}
                      data-testid="template-create-name"
                      className="w-full rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground"
                    />
                  </div>

                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-[11px] text-muted-foreground mb-1">
                        {t("creator:intent.template_primary_label")}
                      </label>
                      <div className="flex gap-1.5">
                        <input
                          type="color"
                          value={newPrimary}
                          onChange={(e) => setNewPrimary(e.target.value)}
                          data-testid="template-create-primary-swatch"
                          className="h-8 w-8 cursor-pointer rounded border border-border bg-transparent"
                        />
                        <input
                          type="text"
                          value={newPrimary}
                          onChange={(e) => setNewPrimary(e.target.value)}
                          data-testid="template-create-primary-hex"
                          className="min-w-0 flex-1 rounded-lg border border-border bg-card px-2 py-1 text-xs text-foreground"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-[11px] text-muted-foreground mb-1">
                        {t("creator:intent.template_secondary_label")}
                      </label>
                      <div className="flex gap-1.5">
                        <input
                          type="color"
                          value={newSecondary}
                          onChange={(e) => setNewSecondary(e.target.value)}
                          data-testid="template-create-secondary-swatch"
                          className="h-8 w-8 cursor-pointer rounded border border-border bg-transparent"
                        />
                        <input
                          type="text"
                          value={newSecondary}
                          onChange={(e) => setNewSecondary(e.target.value)}
                          data-testid="template-create-secondary-hex"
                          className="min-w-0 flex-1 rounded-lg border border-border bg-card px-2 py-1 text-xs text-foreground"
                        />
                      </div>
                    </div>
                  </div>

                  {createError && (
                    <p
                      className="text-xs text-destructive"
                      data-testid="template-create-error"
                    >
                      {createError}
                    </p>
                  )}

                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={resetCreateForm}
                      data-testid="template-create-cancel"
                      className="flex-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                    >
                      {t("creator:intent.template_create_cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateTemplate}
                      disabled={saving || !newName.trim()}
                      data-testid="template-create-save"
                      className="flex-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {t("creator:intent.template_create_save")}
                    </button>
                  </div>
                </div>
              )}

              <p className="px-3 pb-1.5 pt-2 text-[11px] text-muted-foreground">
                {t("creator:intent.template_popover_note")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BrandingTemplateChip;
